import {
    doesFilterPass,
    exitWeekComparator,
    exitWeekValueGetter,
    exitWeekValueParser,
    exitWeekValueSetter,
    ImageCellRenderer,
    infinitySymbol,
    LifeSpanCellEditor,
    ListCellRenderer,
    lifeSpanFilterOptions,
    lifeSpanFormatter,
    lifeSpanValueGetter,
    lifeSpanValueParser,
    lifeSpanValueSetter,
    monthFilterOptions,
    packawayFilterValueGetter,
    packawayValueGetter,
    packawayValueSetter,
    WeekPickerCellEditor,
    WeekRangePickerCellEditor,
    weekFilterOptions,
    weekNumberFormatter,
    weekNumberParser,
    weekRangeNumberFormatter,
    weekRangeNumberParser,
} from '@bestseller/retail-planning.styletimeline';
import {
    ColDef,
    IAggFuncParams,
    IRowNode,
    ValueFormatterParams,
    ValueGetterParams,
    ValueParserParams,
} from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { parseISO } from 'date-fns';
import { isNumber } from 'lodash';
import { sum } from 'ramda';
import { GroupedOption } from 'src/components/atoms/Select/Select';
import { advancedTextColumnFilterOptions } from 'src/components/molecules/AdvancedTextColumnFilter/AdvancedTextColumnFilter';
import { CheckBoxCellRenderer } from 'src/components/molecules/CheckboxCellRenderer/CheckboxCellRenderer';
import { EditableValueCell } from 'src/components/molecules/EditableCell/EditableCell';
import { RatingCell } from 'src/components/molecules/RatingCell/RatingCell';
import { SelectCellEditor } from 'src/components/molecules/SelectCellEditor/SelectCellEditor';
import { StoreCountCellRenderer } from 'src/components/molecules/StoreCountCellRenderer/StoreCountCellRenderer';
import { StyleTimelineOverviewRow, VirtualStyletimelineOverRow } from 'src/domain/table/style-timeline-overview-row';
import { ProductLifeCycleModel } from 'src/infrastructure/rest-api/api-types';
import { isAfterCurrentMonth } from 'src/utils/dateUtils';
import { formatDateAsMonthYear, formatMonthYearAsHumanReadable } from 'src/utils/formatAsMonthYear';
import { formatWeekYearAsHumanReadable } from 'src/utils/formatAsWeekYear';
import { formatNumber } from 'src/utils/formatNumber';
import { numberWithSpaces } from 'src/utils/numberWithSpaces';

export const defaultColDef: ColDef = {
    flex: 0,
    resizable: true,
    sortable: true,
    ...advancedTextColumnFilterOptions,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    tooltipValueGetter: (params) => {
        return params.valueFormatted ?? params.value;
    },
    enablePivot: false,
    enableValue: false,
    filterParams: {
        buttons: ['reset'],
    },
};

export const exitWeekDoesFilterPass: typeof doesFilterPass = (params) => {
    if (!params.model) {
        return true;
    }

    const value = params.handlerParams.getValue(params.node);
    const isInfinite =
        typeof value === 'object' && value !== null && 'exitWeekType' in value && value.exitWeekType === 'INFINITE';

    if (params.model?.type?.value === 6 && isInfinite) {
        return true;
    }

    return doesFilterPass(params);
};

export const columns: ColDef<StyleTimelineOverviewRow & VirtualStyletimelineOverRow>[] = [
    {
        field: 'styleOptionPicture',
        headerName: 'Picture',
        cellRenderer: ImageCellRenderer,
        tooltipValueGetter: () => '',
        suppressFiltersToolPanel: true,
        suppressHeaderMenuButton: true,
        initialWidth: 80,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
    },
    {
        field: 'businessModel',
        headerName: 'Business model',
        cellStyle: {
            justifyContent: 'flex-start',
        },
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'businessModelGroup',
        headerName: 'Business model group',
        cellStyle: {
            justifyContent: 'flex-start',
        },
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'stockAgeBucket',
        headerName: 'Stock age bucket',
        cellStyle: {
            justifyContent: 'flex-start',
        },
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'collections',
        headerName: 'Collections',
        hide: true,
        filter: 'agSetColumnFilter',
        cellRenderer: ListCellRenderer,
        tooltipValueGetter: () => '',
        cellRendererParams: {
            onlyShowMatchesWhenFiltering: true,
        },
        valueFormatter: ({ value }: ValueFormatterParams) => {
            if (!Array.isArray(value)) {
                return value;
            }
            return value?.join(', ');
        },
    },
    {
        field: 'activeSalesCampaigns',
        headerName: 'Active Sales Campaigns',
        hide: true,
        filter: 'agSetColumnFilter',
        cellRenderer: ListCellRenderer,
        tooltipValueGetter: () => '',
        cellRendererParams: {
            onlyShowMatchesWhenFiltering: true,
        },
        valueFormatter: ({ value }: ValueFormatterParams) => {
            if (!Array.isArray(value)) {
                return value;
            }
            return value?.join(', ');
        },
    },
    {
        field: 'exitWeek',
        headerName: 'Exit week',
        editable: true,
        minWidth: 145,
        width: 145,
        valueFormatter: weekNumberFormatter,
        valueParser: exitWeekValueParser,
        cellRenderer: EditableValueCell,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        valueGetter: exitWeekValueGetter,
        valueSetter: exitWeekValueSetter,
        comparator: exitWeekComparator,
        ...weekFilterOptions,
        filter: {
            ...weekFilterOptions.filter,
            doesFilterPass: exitWeekDoesFilterPass,
        },
        filterParams: {
            ...weekFilterOptions.filterParams,
            supportsInfinite: true,
        },
        cellEditor: WeekPickerCellEditor,
        cellEditorParams: {
            formatValue: (value: string) => {
                return weekNumberFormatter({ value } as ValueFormatterParams);
            },
        },
    },
    {
        field: 'normQuantity',
        headerName: 'Norm',
        hide: true,
        comparator: numberComparator,
        cellDataType: 'number',
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'spsWeek',
        headerName: 'SPS pr. week',
        hide: true,
        comparator: numberComparator,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'startWeek',
        headerName: 'Start week',
        cellDataType: 'number',
        valueFormatter: weekNumberFormatter,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        ...weekFilterOptions,
    },
    {
        field: 'startMonth',
        headerName: 'Start month',
        hide: true,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        ...monthFilterOptions,
        cellDataType: 'number',
        comparator: startMonthComparator,
        valueGetter: ({ data }) => {
            if (!data) return '';
            const date = new Date(data.startDate);
            return Number(formatDateAsMonthYear(date));
        },
        valueFormatter: (params) => {
            if (!params.value) return '';
            // the value is always a number (cause valueGetter) unless its a value gotten from the advanced filters input then its a date string like "2025-01-11"
            if (typeof params.value === 'string') {
                return params.value;
            }
            return formatMonthYearAsHumanReadable(params.value);
        },
    },
    {
        field: 'stockQuantity',
        headerName: 'Stock QTY',
        width: 130,
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value || Number.isNaN(cell.value)) return cell.value;
            return numberWithSpaces(cell.value);
        },
    },
    {
        field: 'stockValueLcy',
        headerName: 'Stock value',
        width: 130,
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value || Number.isNaN(cell.value)) return cell.value;
            return numberWithSpaces(cell.value);
        },
        aggFunc: (params: IAggFuncParams) => sum(params.values),
    },
    {
        field: 'stockAgeWeeks',
        headerName: 'Stock age',
        width: 130,
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        cellDataType: 'number',
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value || Number.isNaN(cell.value)) return cell.value;
            return `${numberWithSpaces(cell.value)} weeks`;
        },
    },
    {
        field: 'salesQuantityPast8Weeks',
        headerName: 'Sales QTY P8W',
        hide: true,
        cellStyle: {
            justifyContent: 'flex-end',
            paddingLeft: 45,
        },
        ...advancedTextColumnFilterOptions,
    },
    {
        field: 'styleCategory',
        enableRowGroup: true,
        rowGroup: true,
        rowGroupIndex: 0,
        hide: true,
        headerName: 'Style category',
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data?.styleCategory?.name,
    },
    {
        field: 'styleSubCategoryName',
        hide: true,
        headerName: 'Style sub category',
        filter: 'agSetColumnFilter',
        cellStyle: {
            justifyContent: 'flex-start',
        },
    },
    {
        field: 'styleName',
        headerName: 'Style name',
        cellStyle: {
            justifyContent: 'flex-start',
            fontWeight: 'normal',
        },
        flex: 5,
        minWidth: 300,
        ...advancedTextColumnFilterOptions,
    },
    {
        field: 'styleNumber',
        headerName: 'Style number',
        cellStyle: {
            justifyContent: 'flex-end',
            paddingLeft: 45,
        },
        ...advancedTextColumnFilterOptions,
        cellDataType: 'text',
        sort: 'asc',
    },
    {
        field: 'styleOptionNumber',
        headerName: 'Style option number',
        hide: true,
        cellStyle: {
            justifyContent: 'flex-end',
            paddingLeft: 45,
        },
        ...advancedTextColumnFilterOptions,
    },
    {
        field: 'openOrders',
        headerName: 'Open order QTY',
        hide: true,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
    },
    {
        field: 'grossProfitPast8WeeksLcy',
        headerName: 'Gross Profit P8W',
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        valueFormatter: (cell: ValueFormatterParams) => {
            if (Number.isNaN(cell.value) || !cell.value) return cell.value;
            return numberWithSpaces(Math.round(cell.value));
        },
    },
    {
        field: 'grossProfitRating',
        headerName: 'Gross Profit Rating',
        hide: true,
        comparator: ratingSorting('grossProfitRanking'),
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        cellRenderer: RatingCell,
        tooltipValueGetter: ({ data }) => {
            if (!data?.grossProfitRanking || !data.maxGrossProfitRanking) return null;
            return `Rank ${data.grossProfitRanking} of ${data.maxGrossProfitRanking}`;
        },
    },
    {
        field: 'grossMarginRating',
        headerName: 'Gross Margin Rating',
        hide: true,
        comparator: ratingSorting('grossMarginRanking'),
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        cellRenderer: RatingCell,
        tooltipValueGetter: ({ data }) => {
            if (!data?.grossMarginRanking || !data.maxGrossMarginRanking) return null;
            return `Rank ${data.grossMarginRanking} of ${data.maxGrossMarginRanking}`;
        },
    },
    {
        field: 'grossMarginPast8WeeksLcy',
        headerName: 'Gross Margin P8W',
        hide: true,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        valueFormatter: (cell: ValueFormatterParams) => {
            if (Number.isNaN(cell.value) || !cell.value) return cell.value;
            return `${formatNumber(cell.value, 1)}%`;
        },
    },
    {
        field: 'relativeGrossProfitPast8WeeksLcy',
        headerName: 'Relative Gross Profit P8W',
        hide: true,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        valueFormatter: (cell: ValueFormatterParams) => {
            if (Number.isNaN(cell.value) || !cell.value) return cell.value;
            return `${formatNumber(cell.value, 1)}%`;
        },
    },
    {
        field: 'colorName',
        headerName: 'Color',
        cellStyle: {
            justifyContent: 'flex-start',
        },
        suppressFiltersToolPanel: true,
    },
    {
        field: 'styleFamilyName',
        headerName: 'Style family',
        cellStyle: {
            justifyContent: 'flex-start',
        },
        hide: true,
        suppressFiltersToolPanel: true,
    },
    {
        field: 'styleFit',
        headerName: 'Style fit',
        cellStyle: {
            justifyContent: 'flex-start',
        },
        hide: true,
        suppressFiltersToolPanel: true,
    },
    {
        field: 'packaway',
        headerName: 'Packaway',
        suppressSizeToFit: true,
        editable: true,
        minWidth: 200,
        filterValueGetter: packawayFilterValueGetter,
        valueGetter: packawayValueGetter,
        valueSetter: packawayValueSetter,
        valueFormatter: weekRangeNumberFormatter,
        valueParser: weekRangeNumberParser,
        cellRenderer: EditableValueCell,
        suppressFiltersToolPanel: true,
        cellEditor: WeekRangePickerCellEditor,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'lifeSpan',
        headerName: 'Life span',
        hide: true,
        editable: true,
        ...lifeSpanFilterOptions,
        comparator: exitWeekComparator,
        valueFormatter: lifeSpanFormatter,
        cellRenderer: EditableValueCell,
        cellEditor: LifeSpanCellEditor,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        type: 'numericColumn',
        valueGetter: lifeSpanValueGetter,
        valueSetter: lifeSpanValueSetter,
        valueParser: lifeSpanValueParser,
    },
    {
        field: 'productLineGroup',
        headerName: 'Product Line Group',
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'comment',
        headerName: 'Comment for planners',
        headerTooltip: 'This comment is for other planners, only shown in styletimeline',
        hide: true,
        editable: true,
        ...advancedTextColumnFilterOptions,
        cellRenderer: EditableValueCell,
        cellStyle: {
            justifyContent: 'flex-start',
        },
    },
    {
        field: 'eligibleForSale',
        headerName: 'Eligible For Sale',
        hide: true,
        cellDataType: 'boolean',
        editable: true,
        filter: 'agSetColumnFilter',
        cellStyle: {
            justifyContent: 'flex-end',
        },
        cellRenderer: CheckBoxCellRenderer,
        valueParser({ newValue }) {
            if (typeof newValue === 'string') {
                return newValue.toLowerCase() === 'true';
            }
            return !!newValue;
        },
    },
    {
        field: 'activeStores',
        headerName: 'Store count',
        headerTooltip: 'Number of stores with open sales orders and/or stock',
        hide: true,
        filter: 'agNumberColumnFilter',
        cellRenderer: StoreCountCellRenderer,
        tooltipValueGetter() {
            return null;
        },
        filterValueGetter({ data }) {
            return data?.activeStores?.length ?? 0;
        },
        cellDataType: 'number',
        valueGetter({ data }) {
            return data?.activeStores?.length ?? 0;
        },
    },
    {
        field: 'activeNormStores',
        headerName: 'Norm store count',
        headerTooltip: 'Number of stores with norm > 0',
        hide: true,
        filter: 'agNumberColumnFilter',
        cellRenderer: StoreCountCellRenderer,
        tooltipValueGetter() {
            return null;
        },
        filterValueGetter({ data }) {
            return data?.activeNormStores?.length ?? 0;
        },
        cellDataType: 'number',
        valueGetter({ data }) {
            return data?.activeNormStores?.length ?? 0;
        },
    },
    {
        field: 'openOrdersStores',
        headerName: 'Open order store count',
        headerTooltip: 'Number of stores with open orders',
        hide: true,
        filter: 'agNumberColumnFilter',
        cellRenderer: StoreCountCellRenderer,
        tooltipValueGetter() {
            return null;
        },
        filterValueGetter({ data }) {
            return data?.openOrdersStores?.length ?? 0;
        },
        cellDataType: 'number',
        valueGetter({ data }) {
            return data?.openOrdersStores?.length ?? 0;
        },
    },
    {
        field: 'vmiActiveStores',
        headerName: 'VMI store count',
        headerTooltip: 'Number of stores with a norm count > 0 or open orders > 0',
        hide: true,
        filter: 'agNumberColumnFilter',
        cellRenderer: StoreCountCellRenderer,
        tooltipValueGetter() {
            return null;
        },
        filterValueGetter({ data }) {
            return data?.vmiActiveStores?.length ?? 0;
        },
        cellDataType: 'number',
        valueGetter({ data }) {
            return data?.vmiActiveStores?.length ?? 0;
        },
    },
    {
        field: 'variantName',
        headerName: 'Variant',
        cellStyle: {
            justifyContent: 'flex-start',
        },
        hide: true,
        suppressFiltersToolPanel: true,
    },
    {
        field: 'markDownPct',
        headerName: 'Markdown %',
        width: 130,
        hide: true,
        comparator: numberComparator,
        filter: 'agSetColumnFilter',
        suppressFiltersToolPanel: true,
        valueFormatter: (cell: ValueFormatterParams) => {
            if (Number.isNaN(cell.value) || !cell.value) return cell.value;
            return `${formatNumber(cell.value, 1)}%`;
        },
    },
    {
        field: 'markdownRating',
        headerName: 'Markdown Rating',
        hide: true,
        comparator: ratingSorting('markDownRanking'),
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        cellRenderer: RatingCell,
        tooltipValueGetter: ({ data }) => {
            if (!data?.markDownRanking || !data.maxMarkdownRanking) return null;
            return `Rank ${data.markDownRanking} of ${data.maxMarkdownRanking}`;
        },
    },
    {
        field: 'activeWeeks',
        headerName: 'Active weeks',
        hide: true,
        tooltipValueGetter() {
            return 'Count of weeks with at least one sale of this style';
        },
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
    },
    {
        field: 'sps',
        headerName: 'Sold pr. store',
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
    },
    {
        field: 'spsWeekRating',
        headerName: 'SPS pr. week rating',
        hide: true,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        comparator: ratingSorting('spsWeekRanking'),
        cellRenderer: RatingCell,
        tooltipValueGetter: ({ data }) => {
            if (!data?.spsWeekRanking || !data.maxSpsWeekRanking) return null;
            return `Rank ${data.spsWeekRanking} of ${data.maxSpsWeekRanking}`;
        },
    },
    {
        field: 'recommendedRetailPriceLcy',
        headerName: 'RRP',
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        width: 80,
        valueFormatter: (cell: ValueFormatterParams) => {
            return formatNumber(cell.value, 2);
        },
    },
    {
        field: 'avgNormQuantity',
        headerName: 'Avg. Norm pr. Store',
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        cellDataType: 'number',
        suppressFiltersToolPanel: true,
    },
    {
        field: 'salesIvPast8WeeksLcy',
        headerName: 'Sales iV P8W',
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        valueFormatter: (cell: ValueFormatterParams) => {
            if (Number.isNaN(cell.value)) return cell.value;
            return numberWithSpaces(cell.value);
        },
    },
    {
        field: 'salesP8WRating',
        headerName: 'Sales iV P8W rating',
        hide: true,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        comparator: ratingSorting('salesIvPast8WeeksRanking'),
        cellRenderer: RatingCell,
        tooltipValueGetter: ({ data }) => {
            if (!data?.salesIvPast8WeeksRanking || !data.maxSalesP8WRanking) return null;
            return `Rank ${data.salesIvPast8WeeksRanking} of ${data.maxSalesP8WRanking}`;
        },
    },
    {
        field: 'salesIvPast8WeeksLcySparkline',
        headerName: 'Sales iV P8W Sparkline',
        cellRenderer: 'agSparklineCellRenderer',
        filter: false,
        cellRendererParams: {
            sparklineOptions: {
                tooltip: {
                    renderer: (params: { xValue: string; yValue: string }) => {
                        const xValue = Number.parseInt(params.xValue) - 8;
                        if (xValue === -1) {
                            return {
                                title: isNumber(xValue)
                                    ? `Sales iV ${Math.abs(xValue)} week ago:`
                                    : `Sales iV ? week ago:`,
                                content: params.yValue.toString(),
                            };
                        }
                        return {
                            title: isNumber(xValue)
                                ? `Sales iV ${Math.abs(xValue)} weeks ago:`
                                : `Sales iV ? weeks ago:`,
                            content: params.yValue.toString(),
                        };
                    },
                },
            },
        },
        tooltipValueGetter: () => {
            return null;
        },
        hide: true,
        suppressFiltersToolPanel: true,
    },
    {
        field: 'styleCategorySharePctPast8Weeks',
        headerName: 'Relative Sales iV P8W',
        hide: true,
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        suppressFiltersToolPanel: true,
        valueFormatter: (cell: ValueFormatterParams) => {
            if (Number.isNaN(cell.value) || !cell.value) return cell.value;
            return `${formatNumber(cell.value, 1)}%`;
        },
    },
    {
        field: 'replenishmentEndOfLife',
        headerName: 'End of life',
        hide: true,
        cellDataType: 'number',
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value) return '';
            return formatWeekYearAsHumanReadable(cell.value);
        },
        cellStyle: {
            justifyContent: 'flex-start',
        },
        ...weekFilterOptions,
        headerTooltip: 'Date from Bestplan, when style can no longer be replenished',
    },
    {
        field: 'weekCover',
        headerName: 'Week cover',
        hide: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value) return '';
            return Number.parseFloat(cell.value).toFixed(1).replace('.', ',');
        },
    },
    {
        field: 'stylePriority',
        headerName: 'Style priority',
        suppressSizeToFit: true,
        editable: true,
        minWidth: 200,
        cellRenderer: EditableValueCell,
        suppressFiltersToolPanel: true,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        cellEditor: SelectCellEditor,
        cellEditorParams: {
            selectOptions: [
                {
                    value: '',
                    display: 'None',
                    isHeader: false,
                },
                {
                    value: 'A',
                    display: 'A',
                    isHeader: false,
                },
                {
                    value: 'B',
                    display: 'B',
                    isHeader: false,
                },
                {
                    value: 'C',
                    display: 'C',
                    isHeader: false,
                },
            ],
        },
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'chainFurnitureId',
        headerName: 'Furniture',
        suppressSizeToFit: true,
        editable: true,
        minWidth: 200,
        hide: true,
        cellRenderer: EditableValueCell,
        suppressFiltersToolPanel: true,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        cellEditor: SelectCellEditor,
        cellEditorParams: {
            selectOptions: [],
        },
        filter: 'agSetColumnFilter',
        filterValueGetter: (params) => {
            const chainFurnitures = params.colDef.cellEditorParams?.selectOptions ?? [];
            const displayValue = chainFurnitures.find(
                (option: GroupedOption) => option.value === params.data?.chainFurnitureId?.toString()
            )?.display;

            // Keep deleted references out of Set Filter options.
            return displayValue ?? null;
        },
        valueParser(params: ValueParserParams) {
            if (!params.newValue) return null;

            const chainFurnitures = params.colDef.cellEditorParams?.selectOptions ?? [];

            const option = chainFurnitures.find(
                (option: GroupedOption) => option.value === params.newValue || option.display === params.newValue
            );

            return option ? Number.parseInt(option.value) : null;
        },

        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value) return '';
            const chainFurnitures = cell.colDef.cellEditorParams?.selectOptions ?? [];
            return (
                chainFurnitures.find((option: GroupedOption) => option.value === cell.value?.toString())?.display ??
                '<Deleted>'
            );
        },
    },
    {
        field: 'chainFurnitureZoneId',
        headerName: 'Furniture zone',
        suppressSizeToFit: true,
        editable: true,
        minWidth: 200,
        hide: true,
        cellRenderer: EditableValueCell,
        suppressFiltersToolPanel: true,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        cellEditor: SelectCellEditor,
        cellEditorParams: {
            selectOptions: [],
        },
        filter: 'agSetColumnFilter',
        filterValueGetter: (params) => {
            const chainFurnitureZones = params.colDef.cellEditorParams?.selectOptions ?? [];
            const displayValue = chainFurnitureZones.find(
                (option: GroupedOption) => option.value === params.data?.chainFurnitureZoneId?.toString()
            )?.display;

            // Keep deleted references out of Set Filter options.
            return displayValue ?? null;
        },
        valueParser(params: ValueParserParams) {
            if (!params.newValue) return null;

            const chainFurnitureZones = params.colDef.cellEditorParams?.selectOptions ?? [];

            const option = chainFurnitureZones.find(
                (option: GroupedOption) => option.value === params.newValue || option.display === params.newValue
            );

            return option ? Number.parseInt(option.value) : null;
        },
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value) return '';
            const chainFurnitureZones = cell.colDef.cellEditorParams?.selectOptions ?? [];
            return (
                chainFurnitureZones.find((option: GroupedOption) => option.value === cell.value?.toString())?.display ??
                '<Deleted>'
            );
        },
    },
    {
        field: 'phaseoutProfileId',
        headerName: 'Phaseout profile',
        suppressSizeToFit: true,
        editable: true,
        minWidth: 200,
        cellRenderer: EditableValueCell,
        suppressFiltersToolPanel: true,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        cellEditor: SelectCellEditor,
        cellEditorParams: {
            selectOptions: [],
        },
        hide: true,
        filter: 'agSetColumnFilter',
        filterValueGetter: (params) => {
            const phaseoutProfiles = params.colDef.cellEditorParams?.selectOptions ?? [];
            return (
                phaseoutProfiles.find(
                    (option: GroupedOption) => option.value === params.data?.phaseoutProfileId?.toString()
                )?.display ?? ''
            );
        },
        filterParams: {
            valueFormatter: (cell: ValueFormatterParams) => {
                if (!cell.value) return '(Blanks)';
                return cell.value;
            },
        },
        valueParser(params: ValueParserParams) {
            if (!params.newValue) return null;
            const phaseoutProfiles = params.colDef.cellEditorParams?.selectOptions ?? [];
            const selectedPhaseoutProfile = phaseoutProfiles.find((option: GroupedOption) => {
                return option.value === params.newValue || option.display === params.newValue;
            });
            return selectedPhaseoutProfile?.value ? Number(selectedPhaseoutProfile.value) : null;
        },
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value) return '';
            const phaseoutProfiles = cell.colDef.cellEditorParams?.selectOptions ?? [];
            return (
                phaseoutProfiles.find((option: GroupedOption) => option.value === cell.value?.toString())?.display ?? ''
            );
        },
    },
    {
        field: 'replenishmentPhaseoutMethodId',
        headerName: 'Replenishment phaseout method',
        suppressSizeToFit: true,
        editable: true,
        minWidth: 200,
        cellRenderer: EditableValueCell,
        suppressFiltersToolPanel: true,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        cellEditor: SelectCellEditor,
        cellEditorParams: {
            selectOptions: [],
        },
        hide: true,
        filter: 'agSetColumnFilter',
        filterValueGetter: (params) => {
            const replenishmentPhaseoutMethods = params.colDef.cellEditorParams?.selectOptions ?? [];
            return (
                replenishmentPhaseoutMethods.find(
                    (option: GroupedOption) => option.value === params.data?.replenishmentPhaseoutMethodId?.toString()
                )?.display ?? ''
            );
        },
        filterParams: {
            valueFormatter: (cell: ValueFormatterParams) => {
                if (!cell.value) return '(Blanks)';
                return cell.value;
            },
        },
        valueParser(params: ValueParserParams) {
            if (!params.newValue) return null;
            const replenishmentPhaseoutMethods = params.colDef.cellEditorParams?.selectOptions ?? [];
            const selectedMethod = replenishmentPhaseoutMethods.find((option: GroupedOption) => {
                return option.value === params.newValue || option.display === params.newValue;
            });
            return selectedMethod?.value ? Number(selectedMethod.value) : null;
        },
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value) return '';
            const replenishmentPhaseoutMethods = cell.colDef.cellEditorParams?.selectOptions ?? [];
            return (
                replenishmentPhaseoutMethods.find((option: GroupedOption) => option.value === cell.value?.toString())
                    ?.display ?? ''
            );
        },
    },
    {
        field: 'genderName',
        hide: true,
        headerName: 'Gender',
        cellStyle: {
            justifyContent: 'flex-start',
            fontWeight: 'normal',
        },
        filter: 'agSetColumnFilter',
    },
    {
        field: 'productLineName',
        hide: true,
        headerName: 'Product line',
        cellStyle: {
            justifyContent: 'flex-start',
            fontWeight: 'normal',
        },
        minWidth: 300,
        ...advancedTextColumnFilterOptions,
    },
    {
        field: 'productLifeCycleIds',
        headerName: 'PLC Phase',
        hide: true,
        cellRenderer: ListCellRenderer,
        filter: 'agSetColumnFilter',
        tooltipValueGetter: () => '',
    },
    {
        field: 'activeInBestPlan',
        headerName: 'Active in BestPlan',
        hide: true,
        filter: 'agSetColumnFilter',
        cellDataType: 'boolean',
        filterValueGetter: (params) => {
            return params.data?.activeInBestPlan;
        },
        valueFormatter: (cell: ValueFormatterParams) => {
            return cell.value ? 'Yes' : 'No';
        },
        cellRenderer: ({ valueFormatted }: CustomCellRendererProps) => {
            return valueFormatted;
        },
    },
    {
        field: 'nextKnownDeliveryDate',
        headerName: 'Next known delivery month',
        hide: true,
        cellDataType: 'dateString',
        comparator: startMonthComparator,
        valueGetter({ data }) {
            if (!data?.nextKnownDeliveryDate) return null;
            return Number(formatDateAsMonthYear(parseISO(data.nextKnownDeliveryDate)));
        },
        valueFormatter: (cell: ValueFormatterParams) => {
            if (!cell.value) return '';
            // the value is always a number (cause valueGetter) unless its a value gotten from the advanced filters input then its a date string like "2025-01-11"
            if (typeof cell.value === 'string') {
                return cell.value;
            }
            return formatMonthYearAsHumanReadable(cell.value);
        },
        ...monthFilterOptions,
    },
    {
        field: 'normClosureWeek',
        headerName: 'Norm closure week',
        editable(params) {
            return params.data?.exitWeekType !== 'INFINITE';
        },
        minWidth: 145,
        width: 145,
        hide: true,
        cellDataType: 'number',
        valueFormatter: (params) => {
            if (params.data?.exitWeekType === 'INFINITE') {
                return infinitySymbol;
            }
            return weekNumberFormatter(params);
        },
        valueParser: (params) => {
            // Fill drag converts the raw value to string instead of using the valueFormatter, so we need to handle that case separately.
            // Copy/paste uses the valueFormatter, so we can just use the weekNumberParser there.
            const valueAsNumber = Number(params.newValue);
            if (!Number.isNaN(valueAsNumber)) {
                return valueAsNumber;
            }
            return weekNumberParser(params);
        },
        cellRenderer: EditableValueCell,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        ...weekFilterOptions,
        filterParams: {
            ...weekFilterOptions.filterParams,
            supportsInfinite: false,
        },
        cellEditor: WeekPickerCellEditor,
        cellEditorParams: {
            formatValue: (value: string) => {
                return weekNumberFormatter({ value } as ValueFormatterParams);
            },
        },
    },
    {
        field: 'normClosureWeekExpected',
        headerName: 'Suggested Norm closure week',
        minWidth: 145,
        width: 145,
        hide: true,
        cellDataType: 'number',
        valueFormatter: weekNumberFormatter,
        cellStyle: {
            justifyContent: 'flex-start',
        },
        ...weekFilterOptions,
        filterParams: {
            ...weekFilterOptions.filterParams,
            supportsInfinite: false,
        },
    },
    {
        field: 'averageSalesIvPast1WeekLcy',
        headerName: 'Average Sales iV last week',
        hide: true,
        cellDataType: 'number',
        comparator: numberComparator,
        filter: 'agNumberColumnFilter',
        valueFormatter: (cell: ValueFormatterParams) => {
            return formatNumber(cell.value, 2);
        },
    },
    {
        field: 'isMandatory',
        headerName: 'United Style',
        hide: true,
        editable: true,
        cellDataType: 'boolean',
        filter: 'agSetColumnFilter',
        cellRenderer: CheckBoxCellRenderer,
        valueParser({ newValue }) {
            if (typeof newValue === 'string') {
                return newValue.toLowerCase() === 'true';
            }
            return !!newValue;
        },
    },
    {
        field: 'noosIncoming',
        headerName: 'NOOS Incoming',
        hide: true,
        tooltipValueGetter: () =>
            'If norm quantity > 0 or next known delivery date is after current month then the field is true',
        cellDataType: 'boolean',
        filter: 'agSetColumnFilter',
        valueGetter({ data }) {
            if (!data) return false;
            return (data.normQuantity ?? 0) > 0 || isAfterCurrentMonth(data.nextKnownDeliveryDate);
        },
    },
];

export const globalColumns: ColDef[] = [
    {
        field: 'partner',
        headerName: 'Partner',
        ...advancedTextColumnFilterOptions,
        pinned: 'left',
        enableRowGroup: true,
    },
    {
        field: 'country',
        headerName: 'Country',
        hide: true,
        filter: 'agSetColumnFilter',
    },
    ...columns.map((column) => {
        switch (column.field) {
            case 'styleCategory':
            case 'styleName':
                return {
                    ...column,
                    enableRowGroup: true,
                    rowGroup: true,
                    hide: true,
                };
        }
        return column;
    }),
];

export function numberComparator(a: string | number, b: string | number): number {
    const aNumber = Number(a);
    const bNumber = Number(b);

    if (Number.isNaN(aNumber)) {
        return -1;
    } else if (Number.isNaN(bNumber)) {
        return 1;
    }
    return aNumber - bNumber;
}

export function ratingSorting(
    field: string
): (a: string | number, b: string | number, nodeA: IRowNode, nodeB: IRowNode) => number {
    return (a, b, nodeA, nodeB) => {
        const aNumber = Number(nodeA.data?.[field]);
        const bNumber = Number(nodeB.data?.[field]);

        if (!isNumber(a)) {
            return 1;
        } else if (!isNumber(b)) {
            return -1;
        }

        return aNumber - bNumber;
    };
}

export function startMonthComparator(valueA: number, valueB: number) {
    return numberComparator(valueA, valueB);
}

export const lifecycleValueGetter = (
    params: ValueGetterParams,
    productLifecycleTypes: ProductLifeCycleModel[] | undefined
) => {
    if (!productLifecycleTypes) return;
    const value = params.data?.productLifeCycleIds;

    if (!value) {
        return;
    }

    if (!Array.isArray(value)) {
        const productLifecycleType = productLifecycleTypes?.find((type) => type.id === value);
        return productLifecycleType ? [productLifecycleType.name] : [value];
    }

    return value.map((id: number) => {
        const productLifecycleType = productLifecycleTypes?.find((type) => type.id === id);
        return productLifecycleType ? productLifecycleType.name : id;
    });
};
